/**
 * Error reporting (observability) for the Lumiari Chat Widget — W2-1.
 *
 * Goal: capture unhandled errors / promise rejections originating in the
 * widget and forward them to Sentry, WITHOUT inflating the shipped
 * `dist/widget.js` bundle. The bundle budget is extremely tight
 * (≤ 70 KB min / ≤ 22 KB gzip — see BUNDLE_AUDIT.md), and `@sentry/browser`
 * is ~25–30 KB minified, so it MUST NOT be statically linked into the
 * embed bundle.
 *
 * Design — gated + zero static cost:
 *
 *   - The whole feature is OFF unless a DSN is supplied (via
 *     `config.errorReporting.dsn` or the `data-sentry-dsn` script attr /
 *     `window.LUMIARI_SENTRY_DSN` global). No DSN ⇒ no code path taken,
 *     no network, no SDK load.
 *
 *   - When a DSN IS present we obtain a Sentry client at RUNTIME, never at
 *     build time:
 *       1. If the host page already exposes `window.Sentry` (their own
 *          bundle, or a previously injected CDN copy), reuse it.
 *       2. Otherwise inject the official `@sentry/browser` CDN bundle as a
 *          `<script>` tag (default, zero bytes in our bundle).
 *     Hosts that prefer to self-bundle can pass a `client` directly; the
 *     `@sentry/browser` types are referenced ONLY in a type position
 *     (`import type`), which webpack/ts erases — it never ends up in
 *     `dist/widget.js`. The dependency is declared `optional` in
 *     package.json so `npm install` of the widget does not pull ~30 KB
 *     into consumers who don't opt in.
 *
 * Fail-closed: every method swallows its own errors. A misconfigured DSN,
 * a blocked CDN, or an SDK throw MUST NOT affect widget behavior. This is
 * best-effort telemetry, exactly like TelemetryService.
 *
 * Privacy / PII: we attach only scalar/identifier-shaped tags (widgetKey,
 * agentId, conversation/session ids) — never message content, customer
 * email/address, or payment data. `sendDefaultPii` is forced to false.
 */

// Type-only import: erased at compile time, contributes ZERO bytes to the
// runtime bundle. Provides typing for hosts that bundle @sentry/browser.
import type * as SentryBrowser from '@sentry/browser';

/** Minimal structural subset of the Sentry browser SDK we depend on. */
export interface SentryLike {
  init(options: Record<string, unknown>): unknown;
  captureException(
    exception: unknown,
    hint?: Record<string, unknown>,
  ): string;
  captureMessage?(message: string, level?: string): string;
  setTag?(key: string, value: string): void;
  setContext?(name: string, context: Record<string, unknown> | null): void;
  close?(timeout?: number): Promise<boolean>;
}

export interface ErrorReportingConfig {
  /**
   * Sentry DSN. When falsy the entire feature is disabled — no SDK load,
   * no listeners, no network. This is the master gate.
   */
  dsn?: string | null;
  /** Release identifier, e.g. the widget version. */
  release?: string | null;
  /** deployment environment, e.g. 'production' | 'staging'. */
  environment?: string | null;
  /**
   * Error sample rate 0..1. Defaults to 1 (report all errors). Distinct
   * from performance tracing which we never enable (cost/size).
   */
  sampleRate?: number;
  /**
   * Pre-supplied Sentry client. When the host bundles `@sentry/browser`
   * themselves they can pass `window.Sentry` (or the imported module)
   * here so we skip the CDN injection entirely.
   */
  client?: SentryLike | typeof SentryBrowser | null;
  /**
   * CDN URL for the browser SDK loader. Only used when no `client` is
   * supplied and `window.Sentry` is absent. Pinned by default.
   */
  cdnUrl?: string;
  /**
   * Scalar/identifier tags attached to every event. NEVER PII.
   */
  tags?: Record<string, string | null | undefined>;
  /** Test seam: override the document used for script injection. */
  documentImpl?: Document;
  /** Test seam: resolve the ambient global instead of `window.Sentry`. */
  resolveGlobal?: () => SentryLike | null;
}

// Pinned default. v8 of the loader exposes a global `Sentry`.
const DEFAULT_CDN_URL =
  'https://browser.sentry-cdn.com/8.0.0/bundle.min.js';

export class ErrorReporter {
  private cfg: ErrorReportingConfig;
  private sentry: SentryLike | null = null;
  private enabled: boolean;
  private started = false;
  private loadPromise: Promise<SentryLike | null> | null = null;

  private errorHandler: ((event: ErrorEvent) => void) | null = null;
  private rejectionHandler:
    | ((event: PromiseRejectionEvent) => void)
    | null = null;

  constructor(config: ErrorReportingConfig) {
    this.cfg = config || {};
    // Master gate: a DSN is required for anything to happen.
    this.enabled = !!(this.cfg.dsn && String(this.cfg.dsn).trim());
  }

  /** True only when a DSN was supplied. */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Lazily resolve + init the Sentry SDK and install global listeners.
   * Idempotent and fail-closed. Returns the client (or null when disabled
   * / unavailable). Safe to call in SSR (no `window`) — it no-ops.
   */
  async start(): Promise<SentryLike | null> {
    if (!this.enabled) return null;
    if (typeof window === 'undefined') return null;
    if (this.started) return this.sentry;
    this.started = true;

    try {
      const sentry = await this.resolveSentry();
      if (!sentry) return null;
      this.sentry = sentry;

      sentry.init({
        dsn: this.cfg.dsn,
        release: this.cfg.release || undefined,
        environment: this.cfg.environment || undefined,
        sampleRate:
          typeof this.cfg.sampleRate === 'number'
            ? clampRate(this.cfg.sampleRate)
            : 1,
        // Performance tracing is intentionally disabled — it adds cost and
        // is out of scope for error-reporting.
        tracesSampleRate: 0,
        // Hard privacy guarantee — never auto-collect PII.
        sendDefaultPii: false,
        // Scope events to this widget so they're filterable server-side
        // and don't pollute the host page's own Sentry stream.
        initialScope: {
          tags: { component: 'lumiari-chat-widget' },
        },
      });

      this.applyTags();
      this.installGlobalHandlers();
      return sentry;
    } catch {
      // Swallow — observability must never break the widget.
      return null;
    }
  }

  /**
   * Report an exception explicitly (used by the widget's own catch
   * blocks). No-op when disabled or not yet started.
   */
  captureException(error: unknown, context?: Record<string, unknown>): void {
    if (!this.enabled || !this.sentry) return;
    try {
      this.sentry.captureException(
        error,
        context ? { captureContext: context } : undefined,
      );
    } catch {
      /* swallow */
    }
  }

  /** Flush + remove listeners. Safe to call when never started. */
  async destroy(): Promise<void> {
    if (this.errorHandler && typeof window !== 'undefined') {
      window.removeEventListener('error', this.errorHandler);
      this.errorHandler = null;
    }
    if (this.rejectionHandler && typeof window !== 'undefined') {
      window.removeEventListener('unhandledrejection', this.rejectionHandler);
      this.rejectionHandler = null;
    }
    if (this.sentry && typeof this.sentry.close === 'function') {
      try {
        await this.sentry.close(1000);
      } catch {
        /* swallow */
      }
    }
    this.sentry = null;
  }

  // ---------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------

  private applyTags(): void {
    if (!this.sentry || typeof this.sentry.setTag !== 'function') return;
    const tags = this.cfg.tags || {};
    for (const [key, value] of Object.entries(tags)) {
      if (value == null) continue;
      try {
        this.sentry.setTag(key, String(value));
      } catch {
        /* swallow */
      }
    }
  }

  /**
   * Capture unhandled errors and rejections globally. We forward them to
   * Sentry ourselves rather than relying solely on the SDK's own global
   * hooks, so the events carry our widget tags and so the behavior is
   * deterministic in tests.
   *
   * NOTE: we do NOT preventDefault / stopPropagation — the host page's own
   * error handling (and any other widgets) keep working unchanged.
   */
  private installGlobalHandlers(): void {
    if (typeof window === 'undefined' || !this.sentry) return;

    this.errorHandler = (event: ErrorEvent) => {
      try {
        this.sentry?.captureException(event.error ?? event.message);
      } catch {
        /* swallow */
      }
    };
    this.rejectionHandler = (event: PromiseRejectionEvent) => {
      try {
        this.sentry?.captureException(event.reason);
      } catch {
        /* swallow */
      }
    };

    window.addEventListener('error', this.errorHandler);
    window.addEventListener('unhandledrejection', this.rejectionHandler);
  }

  /**
   * Resolution order: explicit client → host-page global → CDN inject.
   */
  private async resolveSentry(): Promise<SentryLike | null> {
    if (this.cfg.client) return this.cfg.client as SentryLike;

    const ambient = this.readGlobalSentry();
    if (ambient) return ambient;

    return this.loadFromCdn();
  }

  private readGlobalSentry(): SentryLike | null {
    if (this.cfg.resolveGlobal) {
      try {
        return this.cfg.resolveGlobal();
      } catch {
        return null;
      }
    }
    if (typeof window === 'undefined') return null;
    const w = window as unknown as { Sentry?: SentryLike };
    return w.Sentry && typeof w.Sentry.init === 'function' ? w.Sentry : null;
  }

  /**
   * Inject the official Sentry browser CDN bundle as a <script> tag. This
   * is what keeps our own bundle at zero added bytes — the ~30 KB SDK is
   * fetched lazily by the host browser, and only when a DSN is configured.
   */
  private loadFromCdn(): Promise<SentryLike | null> {
    if (this.loadPromise) return this.loadPromise;
    const doc = this.cfg.documentImpl
      || (typeof document !== 'undefined' ? document : null);
    if (!doc) return Promise.resolve(null);

    const url = this.cfg.cdnUrl || DEFAULT_CDN_URL;

    this.loadPromise = new Promise<SentryLike | null>((resolve) => {
      try {
        const script = doc.createElement('script');
        script.src = url;
        script.async = true;
        script.crossOrigin = 'anonymous';
        script.addEventListener('load', () => resolve(this.readGlobalSentry()));
        script.addEventListener('error', () => resolve(null));
        (doc.head || doc.body || doc.documentElement).appendChild(script);
      } catch {
        resolve(null);
      }
    });
    return this.loadPromise;
  }
}

function clampRate(rate: number): number {
  if (!Number.isFinite(rate)) return 1;
  if (rate < 0) return 0;
  if (rate > 1) return 1;
  return rate;
}

/**
 * Resolve a DSN from (in priority order):
 *   1. explicit config.errorReporting.dsn
 *   2. the embed <script> tag's `data-sentry-dsn` attribute
 *   3. `window.LUMIARI_SENTRY_DSN` global
 * Returns null when none present — the master gate stays closed.
 */
export function resolveDsn(
  explicit?: string | null,
  doc?: Document,
  win?: Window,
): string | null {
  if (explicit && String(explicit).trim()) return String(explicit).trim();

  const d = doc || (typeof document !== 'undefined' ? document : undefined);
  if (d) {
    try {
      const tag = d.currentScript as HTMLScriptElement | null;
      const attr = tag?.getAttribute('data-sentry-dsn');
      if (attr && attr.trim()) return attr.trim();
      // Fallback: scan for our embed script.
      const candidate = d.querySelector(
        'script[data-sentry-dsn]',
      ) as HTMLScriptElement | null;
      const candAttr = candidate?.getAttribute('data-sentry-dsn');
      if (candAttr && candAttr.trim()) return candAttr.trim();
    } catch {
      /* ignore */
    }
  }

  const w = win || (typeof window !== 'undefined' ? window : undefined);
  if (w) {
    const dsn = (w as unknown as { LUMIARI_SENTRY_DSN?: string })
      .LUMIARI_SENTRY_DSN;
    if (dsn && String(dsn).trim()) return String(dsn).trim();
  }

  return null;
}
